import {IScanService} from '@techsee/techsee-client-infra/lib/services/ScanService';
import {DashboardAppMediaService} from '../../services/media-service-api/DashboardAppMediaService';
import {KnownMediaStream} from '@techsee/techsee-media-service/lib/MediaConstants';
import {LOG_EVENTS} from '@techsee/techsee-common/lib/constants/event-logs.constants';
import {IEventLogsService} from '../EventsLogService';
import {getRootStore} from '../../app.bootstrap';
import {IHeatmapData} from '../../states/connectivity-guru-data/types';
import {
    IWifiAnalyzerMissingScanIds,
    IWifiAnalyzerService as IWifiAnalyzerApiService,
    IWifiAnalyzerPayload
} from '@techsee/techsee-client-infra/lib/services/WifiAnalyzerService';
import {SESSION_SOURCE} from '@techsee/techsee-common/lib/constants/room.constants';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';

export type Channel = {channel: number; totalAP: number; freq: number};

export interface ISurroundingNetwork {
    channel: number;
    distanceMeters: number;
    frequency: string;
    macAddress: string;
    rssi: number;
    security: string;
    signalStrengthPercentage: number;
    ssid: string;
}

export interface INetworkDiscoveryDevice {
    ip: string;
    macAddress: string;
    manufacturer: string;
    model: string;
    name: string;
}

export interface IWifiConnectionInfo {
    bssids: string[];
    channel: number;
    clientUpdatedOn: Date;
    distanceMeters: number;
    frequency: string;
    macAddress: string;
    rssi: number;
    rxLinkSpeedMbps: number;
    security: string;
    signalStrengthPercentage: number;
    ssid: string;
    txLinkSpeedMbps: number;
}

export interface IScanDetails {
    floor: number;
    note: string;
    roomType: string;
    scanType: string;
}
export interface IInsights {
    wifiInsightsLive: string;
    wifiInsightsCog: string;
    wifiScore: number;
}

export interface IRssiConfig {
    minRSSI: number;
    midRSSI: number;
    maxRSSI: number;
}

export interface IWifiAnalyzerResults {
    _id: string;
    id: string;
    name: string;
    userName?: string;
    description: string;
    customerId?: string;
    deviceModel?: string;
    createdOn: Date;
    updatedAt?: Date;
    accountId: string;
    roomId: string;
    platformType: string;
    active?: boolean;
    wifiConnectionInfo: IWifiConnectionInfo;
    scanAreaResults: any[];
    networkDiscoveryDevices: INetworkDiscoveryDevice[];
    surroundingNetworks: ISurroundingNetwork[];
    networkSpeed: any;
    channelAnalysis: Channel[];
    connectivityGuruData: object;
    connectivityHeatmapData?: IHeatmapData;
    scanDetails: IScanDetails;
    insights?: IInsights;
    rssiConfig?: IRssiConfig;
    sessionSource?: SESSION_SOURCE;
    aggregatedBirthCertificateReportUrl?: string | null;
}

export interface IWifiAnalyzerResultsHistory {
    previews: IWifiAnalyzerResults[];
    totalResults: number;
}

export interface IWifiAnalyzerService {
    runScanner: (
        roomId: string,
        scanId: string,
        onSuccess: (channels: Channel[]) => void,
        onFailure?: () => void
    ) => void;

    getWifiAnalyzerHistory: (
        accountId: string,
        customerId?: string,
        startTime?: Date,
        endTime?: Date,
        pageSize?: number,
        pageNumber?: number,
        preDataCollectionCode?: string,
        scanId?: string,
        customerNumber?: string,
        customerEmail?: string,
        checkAggregatedReport?: boolean
    ) => Promise<IWifiAnalyzerResultsHistory>;

    isWifiAnalyzerExist: (
        accountId: string,
        customerId?: string,
        startTime?: Date,
        endTime?: Date,
        scanId?: string,
        formPreDataCollectionCode?: string
    ) => Promise<boolean>;

    forwardBirthCertificateReport: (
        scanId: string,
        emails: string[],
        reportType: ReportType,
        checkCancelled?: () => boolean
    ) => Promise<void>;
    getScanStatus(scanId: string, reportType: ReportType): Promise<ScanStatusResponse>;
    triggerReportGeneration(scanId: string, reportType: ReportType): Promise<ScanStatusResponse>;
    pollUntilReady(scanId: string, reportType: ReportType, isCancelled?: () => boolean): Promise<string>;
    getMissingWifiScanByExistingScanIds: (
        scanIds: string[],
        customerId: string,
        roomId: string,
        token?: string
    ) => Promise<IWifiAnalyzerMissingScanIds>;
    forwardAggregatedBirthCertificateReport(roomId: string, emails: string[]): Promise<void>;
    getWifiScanResultsForDashboard(
        accountId: string,
        params: {customerId?: string; ivrSessionId?: string; pageSize?: number; pageNumber?: number}
    ): Promise<IWifiAnalyzerResultsHistory>;
}

const MAX_RETRIES = 8;
const POLLING_INTERVAL_MS = 3000;

export enum ScanStatus {
    PENDING = 'pending',
    READY = 'ready'
}

export type ReportType = 'technician' | 'customer' | 'aggregated';

type ScanStatusResponse = {status: ScanStatus.READY; url: string} | {status: ScanStatus.PENDING; url?: undefined};

export class WifiAnalyzerService implements IWifiAnalyzerService {
    private _mediaService: DashboardAppMediaService | null = null;

    constructor(
        private readonly _scannerService: IScanService,
        private readonly _roomsService: IRoomService,
        private readonly _eventsLogService: IEventLogsService,
        private readonly _wifiAnalyzerApiService: IWifiAnalyzerApiService
    ) {}

    public setMediaService = (mediaService: DashboardAppMediaService) => {
        this._mediaService = mediaService;
    };

    public runScanner: IWifiAnalyzerService['runScanner'] = async (roomId, scanId, onSuccess, onFailure) => {
        if (!this._mediaService) {
            throw new Error('Media service is not set');
        }

        this._eventsLogService.info({
            logType: LOG_EVENTS.wifiScanResultScannerRun,
            meta: {scanId, roomId}
        });

        try {
            const result = await this._mediaService.getSnapshotFromKnownStream(KnownMediaStream.USER_VIDEO_STREAM, {
                format: 'image/jpeg'
            });

            this._eventsLogService.info({
                logType: LOG_EVENTS.wifiScanResultSnapshotTaken,
                meta: {result, scanId, roomId}
            });

            const scanResults = await this._scannerService.scanText(result.base64img, roomId);
            const response = scanResults.text?.raw || '';

            this._eventsLogService.info({
                logType: LOG_EVENTS.wifiScanResultScannerResponse,
                meta: {response, scanId, roomId}
            });

            onSuccess(this.parseResults(response));
        } catch (error) {
            this._eventsLogService.error({
                logType: LOG_EVENTS.wifiScanResultScannerError,
                meta: {error, scanId, roomId}
            });

            onFailure?.();
        }
    };

    private parseResults(input: string) {
        const result: Record<string, {channel: number; totalAP: number}[]> = {'2.4': [], '5.0': []};

        const regex = /(\d+\.\d+ GHZ)\s*-?\s*\d+ DEVICES(.*?)(?=\d+\.\d+ GHZ|$)/g;
        let match;

        while ((match = regex.exec(input)) !== null) {
            const channelList = [];
            const deviceList = [];
            let channelMatch;
            let deviceMatch;

            const [_, frequency, channels] = match;
            const channelRegex = /channel (\d+)/g;
            const devicesRegex = /(\d+ devices?)/g;

            while ((channelMatch = channelRegex.exec(channels)) !== null) {
                channelList.push(channelMatch[1]);
            }

            while ((deviceMatch = devicesRegex.exec(channels)) !== null) {
                deviceList.push(deviceMatch[1]);
            }

            for (let i = 0; i < channelList.length; i++) {
                if (deviceList[i] && channelList[i]) {
                    const channel = parseInt(channelList[i].split(' ').pop() || '0');
                    const devices = parseInt(deviceList[i].split(' ').shift() || '0');

                    result[frequency.split(' ')[0]].push({channel, totalAP: devices});
                }
            }
        }

        const initialObject: {channel: number; totalAP: number; freq: number}[] = [];
        const flattenedResults = Object.keys(result).reduce((acc, frequency) => {
            result[frequency].forEach(({channel, totalAP}) => {
                acc.push({channel, totalAP, freq: parseFloat(frequency)});
            });

            return acc;
        }, initialObject);

        return flattenedResults;
    }

    public async getWifiAnalyzerHistory(
        accountId: string,
        customerId?: string,
        startTime?: Date,
        endTime?: Date,
        pageSize?: number,
        pageNumber?: number,
        preDataCollectionCode?: string,
        scanId?: string,
        customerNumber?: string,
        customerEmail?: string,
        checkAggregatedReport?: boolean
    ): Promise<IWifiAnalyzerResultsHistory> {
        if (preDataCollectionCode) {
            try {
                const res = await this._wifiAnalyzerApiService.getByGuidDigits(preDataCollectionCode, {});

                return res as IWifiAnalyzerResultsHistory;
            } catch (error) {
                return {
                    previews: [],
                    totalResults: 0
                };
            }
        }

        const payload = {
            customerId,
            startTime,
            endTime,
            pageSize,
            pageNumber,
            scanId,
            customerNumber,
            customerEmail,
            checkAggregatedReport,
            preDataCollectionCode
        } as IWifiAnalyzerPayload;

        return (await this._wifiAnalyzerApiService.getBulk(accountId, payload)) as IWifiAnalyzerResultsHistory;
    }

    public async getWifiScanResultsForDashboard(
        accountId: string,
        params: {customerId?: string; ivrSessionId?: string; pageSize?: number; pageNumber?: number}
    ): Promise<IWifiAnalyzerResultsHistory> {
        const api = this._wifiAnalyzerApiService as unknown as {
            getWifiScanResultsForDashboard(accountId: string, params: object): Promise<IWifiAnalyzerResultsHistory>;
        };

        return await api.getWifiScanResultsForDashboard(accountId, params);
    }

    public async isWifiAnalyzerExist(
        accountId: string,
        customerId?: string,
        startTime?: Date,
        endTime?: Date,
        scanId?: string,
        formPreDataCollectionCode?: string
    ): Promise<boolean> {
        if (formPreDataCollectionCode) {
            try {
                const res = await this._wifiAnalyzerApiService.getByGuidDigits(formPreDataCollectionCode, {});

                return res.totalResults > 0;
            } catch (error) {
                return false;
            }
        }

        return await this._wifiAnalyzerApiService.isWifiAnalyzerExistForAccount(accountId, {
            customerId,
            startTime,
            endTime,
            scanId
        });
    }

    public async forwardBirthCertificateReport(
        scanId: string,
        emails: string[],
        reportType: ReportType,
        isCancelled?: () => boolean
    ): Promise<void> {
        const result = await this.triggerReportGeneration(scanId, reportType);

        if (isCancelled?.()) {
            throw new Error('Polling cancelled');
        }

        if (result.status === ScanStatus.READY) {
            await this._wifiAnalyzerApiService.forwardBirthCertificateReport(scanId, {emails}, reportType);

            return;
        }

        await this.pollUntilReadyAndForward(scanId, emails, reportType, isCancelled);
    }

    public async triggerReportGeneration(scanId: string, reportType: ReportType): Promise<ScanStatusResponse> {
        const token = getRootStore().authService.getAuthToken();

        return (await this._wifiAnalyzerApiService.generateBirthCertificateReport(
            scanId,
            token,
            reportType
        )) as ScanStatusResponse;
    }

    public async getScanStatus(scanId: string, reportType: ReportType): Promise<ScanStatusResponse> {
        const token = getRootStore().authService.getAuthToken() || '';

        return (await this._wifiAnalyzerApiService.getWifiScanReportStatus(
            scanId,
            token,
            reportType
        )) as ScanStatusResponse;
    }

    private async pollUntilReadyAndForward(
        scanId: string,
        emails: string[],
        reportType: ReportType,
        isCancelled?: () => boolean
    ): Promise<void> {
        await this.pollUntilReady(scanId, reportType, isCancelled);

        await this._wifiAnalyzerApiService.forwardBirthCertificateReport(scanId, {emails}, reportType);
    }

    public async pollUntilReady(scanId: string, reportType: ReportType, isCancelled?: () => boolean): Promise<string> {
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            if (isCancelled?.()) {
                throw new Error('Polling cancelled');
            }

            const scan = await this.getScanStatus(scanId, reportType);

            if (scan.status === ScanStatus.READY) {
                return scan.url;
            }

            await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
        }

        throw new Error(`Polling failed for scanId: ${scanId}, reportType: ${reportType}`);
    }

    public async getMissingWifiScanByExistingScanIds(
        scanIds: string[],
        customerId: string,
        roomId: string,
        token?: string
    ): Promise<IWifiAnalyzerMissingScanIds> {
        try {
            const authToken = token || getRootStore().authService.getAuthToken();

            return this._wifiAnalyzerApiService.getMissingWifiScanByExistingScanIds(
                scanIds,
                customerId,
                roomId,
                authToken
            );
        } catch (error) {
            this._eventsLogService.error({
                logType: LOG_EVENTS.wifiScanHistoryResultsError.type,
                meta: {
                    error,
                    customerId,
                    providedScanIds: scanIds
                }
            });

            throw error;
        }
    }

    public async forwardAggregatedBirthCertificateReport(roomId: string, emails: string[]): Promise<void> {
        return this._wifiAnalyzerApiService.forwardAggregatedBirthCertificateReport(roomId, {emails});
    }
}
